/**
 * Main layout component for authenticated pages
 * Updated to work with smart scroll navbar and bottom navigation
 */

import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import NavBar from '../../shared/components/NavBar';
import BottomNavigation from '../../shared/components/BottomNavigation';
import ProfileCompletionModal from '../../features/profile/components/ProfileCompletionModal';

/**
 * Main layout component
 * 
 * @returns {JSX.Element} Main layout component
 */
const MainLayout = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const [profileComplete, setProfileComplete] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Check if user profile is complete
  useEffect(() => {
    if (userProfile) {
      // Define required fields for a complete profile
      const requiredFields = [
        userProfile.photos && userProfile.photos.length > 0,
        userProfile.basicInfo?.name,
        userProfile.basicInfo?.username,
        userProfile.basicInfo?.email,
        userProfile.academic?.university,
        userProfile.bio?.shortBio,
      ];
      
      // Profile is complete if all required fields are filled
      const isComplete = requiredFields.every(Boolean);
      setProfileComplete(isComplete);
      
      // Show profile completion modal if profile is incomplete and not on profile page
      if (!isComplete && location.pathname !== '/profile') {
        setShowProfileModal(true);
      } else {
        setShowProfileModal(false);
      }
    }
  }, [userProfile, location.pathname]);
  
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Navigation Bar - The NavBar component now handles its own fixed positioning and scrolling behavior */}
      <NavBar />
      
      {/* Main Content - Added bottom padding for mobile bottom navigation */}
      <main className="flex-grow container mx-auto px-4 py-6 pb-20 md:pb-6">
        <Outlet />
      </main>
      
      {/* Bottom Navigation (Mobile Only) */}
      <BottomNavigation />
      
      {/* Profile completion modal */}
      {showProfileModal && (
        <ProfileCompletionModal onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
};

export default MainLayout;